import React, { useState } from 'react';
import { ElementGeneration } from '../../api/apiTypes';
import { fetchElementGenerations } from '../../api/functionApi';

interface Props {
  reportId: string;
  componentId: string;
  elementId: string;
}

const STATO: Record<ElementGeneration['status'], string> = {
  claimed: 'Calcolo in corso',
  computed: 'Calcolato',
  reused: 'Riusato da un calcolo precedente',
  failed: 'Fallito',
};

/**
 * "Come è stato prodotto questo valore": storico delle generazioni di un elemento,
 * dal più recente. Si carica solo all'apertura — è un pannello di consultazione,
 * non serve al flusso.
 */
const ElementGenerationsPanel: React.FC<Props> = ({ reportId, componentId, elementId }) => {
  const [aperto, setAperto] = useState(false);
  const [generazioni, setGenerazioni] = useState<ElementGeneration[]>([]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');

  const apri = async () => {
    setAperto(true);
    if (generazioni.length > 0 || loading) return;

    setLoading(true);
    setErrore('');
    try {
      setGenerazioni(await fetchElementGenerations(reportId, componentId, elementId));
    } catch (err) {
      console.error('Storico generazioni non caricabile:', err);
      setErrore(err instanceof Error ? err.message : 'Storico non disponibile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <details
      className="card-bg p-8 rounded-lg shadow-md mt-8"
      onToggle={(e) => ((e.target as HTMLDetailsElement).open ? apri() : setAperto(false))}
    >
      <summary className="cursor-pointer text-xl font-semibold text-textPrimary font-heading">
        Come è stato prodotto
      </summary>

      {aperto && (
        <div className="mt-4">
          {loading && <p className="text-sm text-textSecondary">Caricamento…</p>}
          {errore && <p className="text-sm text-red-700">{errore}</p>}

          {!loading && !errore && generazioni.length === 0 && (
            <p className="text-sm text-textSecondary">Nessuna generazione registrata.</p>
          )}

          <div className="space-y-3">
            {generazioni.map((g) => (
              <div key={g.id} className="border border-borderDivider rounded-md p-4 bg-white">
                <div className="flex justify-between gap-2 items-start">
                  <span className="font-semibold text-sm text-gray-700">
                    {STATO[g.status] ?? g.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(g.completed_at ?? g.created_at).toLocaleString('it-IT')}
                  </span>
                </div>

                {/* `applied: false` su un riuso: il valore in cache c'era, ma
                    l'elemento era già stato modificato a mano e non è stato toccato. */}
                {g.status === 'reused' && g.applied === false && (
                  <p className="text-xs text-gray-600 mt-1">
                    Valore in cache non applicato: l'elemento era già stato modificato a mano.
                  </p>
                )}
                {g.error && <p className="text-xs text-red-700 mt-1">{g.error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </details>
  );
};

export default ElementGenerationsPanel;
